/* eslint-disable camelcase */
import { gql } from '@apollo/client'

export type {
  LotDashboard,
  LotDashboardVariables,
  LotDashboard_lot_LotV2_riceLot_RiceLot_appliedFertilizers_RiceAppliedFertilizer as AppliedFertilizer,
  LotDashboard_lot_LotV2_riceLot_RiceLot_appliedFertilizers_RiceAppliedFertilizer_grades_FertilizerGrade as FertilizerGrades,
  LotDashboard_lot_LotV2_riceLot_RiceLot_appliedPhytosanitaries_RiceAppliedPhytosanitary as AppliedPhytosanitaries,
  LotDashboard_lot_LotV2_riceLot_RiceLot_indicators_Indicators_grainHumidity_GrainHumidity as GrainHumidity,
  LotDashboard_lot_LotV2_riceLot_RiceLot_estimatedYield_EstimatedYield as EstimatedYield,
  LotDashboard_lot_LotV2_riceLot_RiceLot_historicalIrrigation_HistoricalIrrigation as HistoricalIrrigation,
} from './types/index.graphql'

export type { CreateRiceFertilizerVariables, CreateRiceFertilizer } from './types/index.graphql'
export type { DeleteRiceFertilizerVariables, DeleteRiceFertilizer } from './types/index.graphql'

export type {
  RiceUnitsNutrientsAndIngredients,
  RiceUnitsNutrientsAndIngredients_riceUnits_RiceUnits_results_RiceUnit as RiceUnit,
  CreateRiceFertilizer_createRiceFertilizer_RiceAppliedFertilizerMutationResponse_fertilizer_RiceAppliedFertilizer_grades_FertilizerGrade_nutrient_RiceNutrient as RiceNutrient,
  LotDashboard_lot_LotV2_riceLot_RiceLot_appliedPhytosanitaries_RiceAppliedPhytosanitary_activeIngredients_RiceActiveIngredient as RiceActiveIngredient,
  RiceUnitsNutrientsAndIngredientsVariables,
} from './types/index.graphql'

export type {
  CreateRicePhytosanitaryVariables,
  CreateRicePhytosanitary,
} from './types/index.graphql'

export type {
  DeleteRicePhytosanitaryVariables,
  DeleteRicePhytosanitary,
} from './types/index.graphql'

export const LOT_DASHBOARD = gql`
  query LotDashboard($id: Int!, $isSatelliteImageryFlagOn: Boolean!) {
    lot(id: $id) {
      id
      name
      size
      commercialName
      area {
        coordinates
        type
      }
      previousCrop {
        id
      }
      variety {
        id
        name
        isGrainHumidityAbsolute
        locales {
          es
          en
          pt
        }
      }
      crop {
        id
        name
        cropType
        locales {
          es
          en
          pt
        }
      }
      productionType
      abandonedSize
      riceLot {
        targetYieldCurve @include(if: $isSatelliteImageryFlagOn) {
          id
          name
          values {
            daysSinceEmergence
            chlorophyll
          }
        }
        historicalIrrigation @include(if: $isSatelliteImageryFlagOn) {
          values {
            date
            percentage
            daysSinceEmergence
          }
        }
        realYieldCurve @include(if: $isSatelliteImageryFlagOn) {
          values {
            daysSinceEmergence
            chlorophyll
          }
        }
        id
        emergenceDate
        emergenceUpdateType
        plantingDate
        plantingUpdateType
        firstIrrigationStartDate
        firstIrrigationEndDate
        floweringDate
        floweringUpdateType
        harvestDate
        harvestUpdateType
        preparationDate
        preparationUpdateType
        burningDate
        burningUpdateType
        earAppearanceDate
        earAppearanceUpdateType
        physiologicalMaturityDate
        differentiationDate
        differentiationUpdateType
        daysSinceEmergence
        isHarvested
        comment
        yield
        indicators @include(if: $isSatelliteImageryFlagOn) {
          semaphore
          sufficientWaterPercentage
          irrigation
          chlorophyll
          weatherType
          lastUpdate
          grainHumidity {
            isAvailable
            lastUpdate
            emergenceDaysActivation
            levels {
              one {
                lowEdge
                highEdge
                percentage
              }
              two {
                lowEdge
                highEdge
                percentage
              }
              three {
                lowEdge
                highEdge
                percentage
              }
              four {
                lowEdge
                highEdge
                percentage
              }
              five {
                lowEdge
                highEdge
                percentage
              }
              six {
                lowEdge
                highEdge
                percentage
              }
              seven {
                lowEdge
                highEdge
                percentage
              }
            }
          }
        }
        appliedFertilizers @include(if: $isSatelliteImageryFlagOn) {
          id
          name
          brand
          date
          quantity
          unit {
            id
          }
          grades {
            grade
            nutrient {
              id
              name
              symbol
            }
          }
        }
        appliedPhytosanitaries @include(if: $isSatelliteImageryFlagOn) {
          id
          name
          brand
          date
          quantity
          unit {
            id
          }
          activeIngredients {
            id
            name
            locales {
              es
              en
              pt
            }
          }
        }
        estimatedYield {
          isAvailable
          value
          lotValue
        }
      }
    }
  }
`

export const CREATE_RICE_FERTILIZER = gql`
  mutation CreateRiceFertilizer($createFertilizerDTO: CreateRiceAppliedFertilizerInput!) {
    createRiceFertilizer(createFertilizerDTO: $createFertilizerDTO) {
      code
      fertilizer {
        id
        name
        brand
        date
        quantity
        unit {
          id
          name
          locales {
            es
            en
            pt
          }
        }
        grades {
          grade
          nutrient {
            id
            name
            symbol
            locales {
              es
              en
              pt
            }
          }
        }
      }
    }
  }
`

export const DELETE_RICE_FERTILIZER = gql`
  mutation DeleteRiceFertilizer($id: Int!) {
    deleteRiceFertilizer(id: $id) {
      success
    }
  }
`

export const RICE_UNITS_NUTRIENTS_AND_INGREDIENTS = gql`
  query RiceUnitsNutrientsAndIngredients($countryId: CountryId!) {
    riceUnits(countryId: $countryId) {
      results {
        id
        locales {
          es
          en
          pt
        }
      }
    }

    riceNutrients(countryId: $countryId) {
      results {
        id
        name
        symbol
        locales {
          es
          en
          pt
        }
      }
    }

    riceActiveIngredients(countryId: $countryId) {
      results {
        id
        name
        locales {
          es
          en
          pt
        }
      }
    }
  }
`

export const CREATE_RICE_PHYTOSANITARY = gql`
  mutation CreateRicePhytosanitary($createPhytosanitaryDTO: CreateRiceAppliedPhytosanitaryInput!) {
    createRicePhytosanitary(createPhytosanitaryDTO: $createPhytosanitaryDTO) {
      code
      phytosanitary {
        id
        name
        brand
        date
        quantity
        unit {
          id
          name
          locales {
            es
            en
            pt
          }
        }
        activeIngredients {
          id
          name
          locales {
            es
            en
            pt
          }
        }
      }
    }
  }
`
export const DELETE_RICE_PHYTOSANITARY = gql`
  mutation DeleteRicePhytosanitary($id: Int!) {
    deleteRicePhytosanitary(id: $id) {
      success
    }
  }
`
